import React from "react";
import { Chip } from "@/components/badge/chip";
import { POST_CARD_CLASS } from "@/components/thread/post-link";

import useSWR from "swr";
import { useApps } from "@/hooks/apps";
import { usePrdReportDocs } from "@/hooks/use-report-docs";
import { ReportDeleteDialog } from "@/components/reports/report-doc-atoms";
import { ReportDocRow } from "@/components/reports/report-doc-row";
import type { App, Prd } from "@/types/domain";

// PRD reports as thread-look items (owner 2026-08-18): read/manage only —
// the add entry retired with the standalone Reports section; reports are
// created from the version flow / report dialogs elsewhere.
const ReportDocCell = ({ prd }: { prd: Prd }) => {
  const Apps = useApps();

  const reportDocs = prd.reportDocs || [];

  const docs = usePrdReportDocs(prd.id, reportDocs);

  // The PRD's scheduled engagements give app + version name per version id.
  // Prefer the engagements on the passed-in prd; fetch the detail otherwise.
  const prdWithEngagements = prd?.engagements ? prd : null;
  const { data: prdDetail } = useSWR<Prd>(
    prdWithEngagements ? null : [`/prds/${prd.id}`]
  );
  const versionsMap = React.useMemo(() => {
    const es = prdWithEngagements?.engagements || prdDetail?.engagements;
    if (!es) return {};
    const map: Record<string, { appName: string; docName?: string }> = {};
    es.forEach((e) => {
      if (!e.versionId) return;
      const app = Apps.find((app: App) => app.code === e.appId);
      map[e.versionId] = {
        appName: app?.name || String(e.appId ?? ""),
        docName: e.versionName ?? undefined,
      };
    });
    return map;
  }, [prdWithEngagements, prdDetail, Apps]);

  return (
    <div className="flex flex-col gap-2">
      {reportDocs.length > 0 && (
        <div className="flex flex-col gap-2">
          {/* Thread-item styling (owner 2026-08-18): each report is a
              post-look card, marked by a Report chip. */}
          {reportDocs.map((doc) => (
            <div key={doc.url} className={POST_CARD_CLASS}>
              <div className="flex items-center gap-2 px-3 py-2">
                <Chip tone="purple" className="shrink-0">
                  Report
                </Chip>
                <div className="min-w-0 flex-1">
                  <ReportDocRow
                    doc={doc}
                    docs={docs}
                    actions="menu"
                    appTag={
                      doc.versionId ? versionsMap[doc.versionId] : undefined
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Single delete confirmation, opened by a row's More → Delete. */}
      <ReportDeleteDialog
        open={docs.deleteUrl != null}
        onCancel={() => docs.askDelete(null)}
        onConfirm={docs.confirmDelete}
        isMutating={docs.isMutating}
      />
    </div>
  );
};

export default ReportDocCell;
